import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth-context";
import { cn } from "@/lib/utils";
import {
  buildPlanCatalog,
  FREE_PLAN_SUMMARY,
  type BillingInterval,
  type PlanCatalogEntry,
} from "@/lib/billing-plans";

const INTERVALS: readonly { value: BillingInterval; label: string }[] = [
  { value: "month", label: "Mensal" },
  { value: "year", label: "Anual" },
];

interface IntervalToggleProps {
  value: BillingInterval;
  onChange: (value: BillingInterval) => void;
  savings: string | null;
}

function IntervalToggle({ value, onChange, savings }: IntervalToggleProps): JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-4">
      <div
        role="radiogroup"
        aria-label="Período de cobrança"
        className="relative grid grid-cols-2 rounded-full border border-border-strong bg-surface p-1"
      >
        <span
          aria-hidden="true"
          className="absolute inset-y-1 left-1 w-[calc(50%-4px)] rounded-full bg-signal transition-transform duration-300 ease-[cubic-bezier(0.23,1,0.32,1)]"
          style={{ transform: value === "year" ? "translateX(100%)" : "translateX(0)" }}
        />
        {INTERVALS.map((item) => (
          <button
            key={item.value}
            type="button"
            role="radio"
            aria-checked={value === item.value}
            onClick={() => onChange(item.value)}
            className={cn(
              "relative z-10 min-w-[6.5rem] rounded-full px-5 py-2 text-sm font-semibold transition-colors duration-200",
              value === item.value ? "text-signal-foreground" : "text-muted-foreground hover:text-foreground",
            )}
          >
            {item.label}
          </button>
        ))}
      </div>
      {savings && <p className="text-sm text-muted-foreground">No anual, economize {savings}.</p>}
    </div>
  );
}

function Features({ items }: { items: readonly string[] }): JSX.Element {
  return (
    <ul className="mt-8 space-y-3 text-[0.9375rem]">
      {items.map((feature) => (
        <li key={feature} className="flex gap-3">
          <Check size={17} className="mt-0.5 shrink-0 text-signal" aria-hidden="true" />
          <span className="text-foreground/90">{feature}</span>
        </li>
      ))}
    </ul>
  );
}

function FreeColumn(): JSX.Element {
  return (
    <article className="flex flex-col rounded-2xl border border-border bg-surface p-7">
      <h3 className="text-xl font-semibold">{FREE_PLAN_SUMMARY.name}</h3>
      <p className="mt-2 min-h-[3rem] text-[0.9375rem] leading-relaxed text-muted-foreground">{FREE_PLAN_SUMMARY.tagline}</p>
      <p className="mt-6 flex items-baseline gap-1.5">
        <span className="text-4xl font-bold tracking-[-0.04em] tabular-nums">R$ 0</span>
        <span className="text-muted-foreground">para sempre</span>
      </p>
      <p className="mt-1 h-5 text-sm text-muted-foreground">Sem cartão</p>
      <Features items={FREE_PLAN_SUMMARY.features} />
      <div className="mt-auto pt-10">
        <Button asChild variant="outline" size="lg" className="lp-press w-full">
          <Link to="/cadastro">Criar conta grátis</Link>
        </Button>
      </div>
    </article>
  );
}

function PaidColumn({ entry, interval }: { entry: PlanCatalogEntry; interval: BillingInterval }): JSX.Element {
  const price = entry.prices[interval];
  // Logado vai direto para os planos do painel; visitante cria a conta antes
  // (o cadastro ainda não recebe o plano escolhido).
  const { session } = useAuth();
  const href = session ? "/app/plans" : "/cadastro";
  return (
    <article
      className={cn(
        "relative flex flex-col rounded-2xl border p-7",
        entry.highlighted ? "border-signal/60 bg-signal-dim/30" : "border-border bg-surface",
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-xl font-semibold">{entry.name}</h3>
        {entry.highlighted && (
          <span className="rounded-full bg-signal px-2.5 py-0.5 text-xs font-semibold text-signal-foreground">
            Recomendado
          </span>
        )}
      </div>
      <p className="mt-2 min-h-[3rem] text-[0.9375rem] leading-relaxed text-muted-foreground">{entry.tagline}</p>
      <p className="mt-6 flex items-baseline gap-1.5" aria-live="polite">
        <span className="text-4xl font-bold tracking-[-0.04em] tabular-nums">{price.amountLabel}</span>
        <span className="text-muted-foreground">{price.periodLabel}</span>
      </p>
      <p className="mt-1 h-5 text-sm text-muted-foreground">
        {price.monthlyEquivalentLabel ? `Equivale a ${price.monthlyEquivalentLabel.replace("~", "")}` : "Cobrança mensal"}
      </p>
      <Features items={entry.features} />
      <div className="mt-auto pt-10">
        <Button asChild variant={entry.highlighted ? "signal" : "outline"} size="lg" className="lp-press w-full">
          <Link to={href}>Assinar {entry.name}</Link>
        </Button>
      </div>
    </article>
  );
}

export function Precos(): JSX.Element {
  const [interval, setBillingInterval] = useState<BillingInterval>("month");
  const catalog = useMemo(() => buildPlanCatalog(), []);
  const savings = catalog.find((entry) => entry.highlighted)?.prices.year.savingsPercent ?? null;

  return (
    <section id="precos" aria-labelledby="precos-heading" className="scroll-mt-20 border-t border-border">
      <div className="mx-auto max-w-[77.5rem] px-4 py-24 sm:px-6 lg:px-8">
        <h2
          id="precos-heading"
          className="text-[clamp(2rem,1.4rem+2.2vw,3.25rem)] font-bold leading-[1.05] tracking-[-0.035em]"
        >
          Você paga a assinatura. A comissão é toda sua.
        </h2>
        <div className="mt-8">
          <IntervalToggle value={interval} onChange={setBillingInterval} savings={savings} />
        </div>

        <div className="mt-12 grid gap-4 lg:grid-cols-3">
          <FreeColumn />
          {catalog.map((entry) => (
            <PaidColumn key={entry.plan} entry={entry} interval={interval} />
          ))}
        </div>
      </div>
    </section>
  );
}
